import { api } from './client';
import type { Page } from './catalogs';

export type ReceiptOperationType =
  | 'FROM_BUYER' | 'FROM_BANK' | 'OTHER' | 'LOAN' | 'LOAN_GIVEN_RETURN' | 'CARD_RECEIPT' | 'CURRENCY_EXCHANGE'
  | 'REFUND_FROM_SUPPLIER' | 'TRANSFER_OWN'  // ч.827
  | 'CAPITAL_CONTRIBUTION'  // ч.835
  | 'TRANSIT_IN'            // ч.941: транзит комісії/доручення (Дт 311 Кт 685)
  | 'OWNER_CONTRIBUTION';   // ч.1133: ФОП вносить власні кошти (Дт 311 Кт 443) — не дохід
export type PaymentOperationType =
  | 'TO_SUPPLIER' | 'TO_BANK' | 'TAX' | 'SALARY' | 'LOAN_RETURN' | 'LOAN_GIVEN' | 'TRANSIT_OUT' | 'OTHER'
  | 'CARD_PAYMENT' | 'CURRENCY_EXCHANGE'
  | 'REFUND_TO_BUYER' | 'TRANSFER_OWN' | 'TO_PODOTCHET'  // ч.827
  | 'OWNER_WITHDRAWAL';     // ч.1133: ФОП виводить кошти на власну картку (Дт 443 Кт 311)

// Block 5.3a (2026-05-27): CancellableMixin state.
export type CancellableState = 'DRAFT' | 'POSTED' | 'CANCELLED';

// ----- BankReceipt -----

export interface BankReceiptListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  bank_account_id: string;
  contractor_id: string | null;
  /** ч.1182: назва платника з сервера (клієнтська мапа контрагентів неповна). */
  contractor_name?: string | null;
  // ч.795: нерозпізнане з виписки банку (API/файл) — бейдж ⚠ і фільтр.
  needs_review?: boolean;
  bank_ref?: string | null;
  operation_type: string;
  amount: string;
  // ч.798: валютний рахунок — сума у валюті + курс; назва рахунку і код валюти для колонок/фільтрів.
  amount_currency?: string | null;
  exchange_rate?: string | null;
  bank_account_name?: string | null;
  currency_code?: string | null;
  payment_purpose: string | null;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  project_id?: string | null;
  // Block 5.3a:
  state?: CancellableState;
  // 2026-08-05: контроль авансових ПН (привʼязана авансова реєстрація).
  adv_id?: string | null;
  adv_posted?: boolean | null;
  adv_pn_id?: string | null;
  // ч.1447: ПН на аванс, складена прямо з надходження.
  pn_id?: string | null;
  pn_posted?: boolean | null;
  pn_number?: string | null;
}

export interface BankReceipt extends BankReceiptListItem {
  // ч.731 (0478): повна картка з 1С.
  paid_at?: string | null;
  department_id?: string | null;
  responsible_name?: string | null;
  tax_purpose_id?: string | null;
  vat_account_code?: string | null;
  vat_account_confirmed_code?: string | null;
  vat_rate_id?: string | null;
  vat_amount?: string | null;
  operation_kind_1c?: string | null;
  settlement_doc_1c?: string | null;

  contract_id: string | null;
  customer_order_id: string | null;
  goods_sale_id?: string | null;  // ч.752: «На підставі» — реалізація
  production_service_act_id?: string | null;  // ч.908
  processing_services_sale_id?: string | null;  // ч.908
  incoming_doc_number: string | null;
  incoming_doc_date: string | null;
  payer_name: string | null;
  payer_edrpou: string | null;
  payer_iban: string | null;
  payer_bank_mfo: string | null;
  cash_flow_item_id: string | null;
  comment: string | null;
  posted_at: string | null;
  // Block 5.3a: cancellation fields.
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface BankReceiptCreate {
  organization_id: string;
  // ч.731 (0478): повна картка.
  paid_at?: string | null;
  department_id?: string | null;
  responsible_name?: string | null;
  tax_purpose_id?: string | null;
  vat_account_code?: string | null;
  vat_account_confirmed_code?: string | null;
  vat_rate_id?: string | null;
  vat_amount?: string | null;
  document_number: string;
  document_date: string;
  bank_account_id: string;
  operation_type: ReceiptOperationType;
  contractor_id?: string | null;
  contract_id?: string | null;
  customer_order_id?: string | null;
  goods_sale_id?: string | null;
  production_service_act_id?: string | null;  // ч.908
  processing_services_sale_id?: string | null;  // ч.908
  payer_name?: string | null;
  payer_edrpou?: string | null;
  payer_iban?: string | null;
  amount: string;
  amount_currency?: string | null;  // ч.798
  exchange_rate?: string | null;
  payment_purpose?: string | null;
  comment?: string | null;
  incoming_doc_number?: string | null;
  incoming_doc_date?: string | null;
}

export type BankReceiptUpdate = Partial<BankReceiptCreate>;

export const bankReceipts = {
  list: (params?: { organization_id?: string; contractor_id?: string; posted?: boolean; limit?: number
    needs_review?: boolean; bank_account_id?: string; currency_code?: string; operation_type?: string;
    date_from?: string;
    date_to?: string;
    pn_link?: 'with' | 'without';
    offset?: number;
    sort_by?: string;
    sort_dir?: 'asc' | 'desc'; }) =>
    api.get<Page<BankReceiptListItem>>('/bank-receipts', { params }).then((r) => r.data),
  // ч.797: підсумок (кількість+сума) під ті ж фільтри — серверна пагінація.
  tabCounts: (organizationId?: string) =>
    api.get<{ review: number; saved: number }>('/bank-receipts/tab-counts', { params: { organization_id: organizationId || undefined } }).then((r) => r.data),
  summary: (params?: { organization_id?: string; contractor_id?: string; posted?: boolean;
    date_from?: string; date_to?: string; needs_review?: boolean; pn_link?: 'with' | 'without';
    bank_account_id?: string; currency_code?: string }) =>
    api.get<{ count: number; sum_amount: string }>('/bank-receipts/summary', { params }).then((r) => r.data),
  get: (id: string) => api.get<BankReceipt>(`/bank-receipts/${id}`).then((r) => r.data),
  create: (payload: BankReceiptCreate) =>
    api.post<BankReceipt>('/bank-receipts', payload).then((r) => r.data),
  update: (id: string, payload: BankReceiptUpdate) =>
    api.patch<BankReceipt>(`/bank-receipts/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/bank-receipts/${id}`).then(() => undefined),
  post: (id: string) => api.post<BankReceipt>(`/bank-receipts/${id}/post`).then((r) => r.data),
  unpost: (id: string) => api.post<BankReceipt>(`/bank-receipts/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/bank-receipts/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};

// ----- BankPayment -----

export interface BankPaymentListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  bank_account_id: string;
  contractor_id: string | null;
  operation_type: string;
  amount: string;
  // ч.798: валютний рахунок — сума у валюті + курс; назва рахунку і код валюти для колонок/фільтрів.
  amount_currency?: string | null;
  exchange_rate?: string | null;
  bank_account_name?: string | null;
  currency_code?: string | null;
  payment_purpose: string | null;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  project_id?: string | null;
  // Block 5.3a:
  state?: CancellableState;
  // 2026-08-04: контроль вхідних ПН по авансах (ПН привʼязана до платежу).
  pn_id?: string | null;
  pn_number?: string | null;
  pn_is_registered?: boolean | null;
  pn_external_source?: string | null;
  pn_registration_date?: string | null;
  contractor_is_vat_payer?: boolean;
  // ч.776: отримувач для списку (контрагент / працівник / текст).
  recipient_display?: string | null;
  // ч.727: статус чернетки («відправлено в банк») + стаття ДДС.
  sent_to_bank_at?: string | null;
  cash_flow_item_id?: string | null;
  // ч.739: авто-виписка через API банку.
  needs_review?: boolean;
  bank_api_ref?: string | null;
  bank_ref?: string | null;
  // ч.766: зарплатна платіжка без привʼязки до податкових — не відбирається.
  operation_type_tax_links_missing?: boolean;
  tax_po_pdfo_id?: string | null;
  tax_po_wz_id?: string | null;
  tax_po_esv_id?: string | null;
}

export interface BankPayment extends BankPaymentListItem {
  bank_order_number?: string | null;
  // ч.731 (0478): повна картка з 1С.
  paid_at?: string | null;
  department_id?: string | null;
  responsible_name?: string | null;
  tax_purpose_id?: string | null;
  vat_account_code?: string | null;
  vat_account_confirmed_code?: string | null;
  vat_rate_id?: string | null;
  vat_amount?: string | null;
  operation_kind_1c?: string | null;
  settlement_doc_1c?: string | null;
  contract_id: string | null;
  supplier_order_id: string | null;
  goods_receipt_id?: string | null;  // ч.752: «На підставі» — надходження
  employee_id: string | null;
  payroll_payment_id?: string | null;
  tax_remittance_id?: string | null;
  tax_po_pdfo_id?: string | null;
  tax_po_wz_id?: string | null;
  tax_po_esv_id?: string | null;
  recipient_name: string | null;
  recipient_edrpou: string | null;
  recipient_iban: string | null;
  recipient_bank_mfo: string | null;
  cash_flow_item_id: string | null;
  comment: string | null;
  posted_at: string | null;
  // Block 5.3a: cancellation fields.
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface BankPaymentCreate {
  organization_id: string;
  // ч.731 (0478): повна картка.
  paid_at?: string | null;
  department_id?: string | null;
  responsible_name?: string | null;
  tax_purpose_id?: string | null;
  vat_account_code?: string | null;
  vat_account_confirmed_code?: string | null;
  vat_rate_id?: string | null;
  vat_amount?: string | null;
  bank_order_number?: string | null;
  document_number: string;
  document_date: string;
  bank_account_id: string;
  operation_type: PaymentOperationType;
  contractor_id?: string | null;
  contract_id?: string | null;
  supplier_order_id?: string | null;
  goods_receipt_id?: string | null;
  employee_id?: string | null;
  // ч.754: «На підставі» для ЗП/податків (link-таблиці).
  payroll_payment_id?: string | null;
  tax_remittance_id?: string | null;
  tax_po_pdfo_id?: string | null;
  tax_po_wz_id?: string | null;
  tax_po_esv_id?: string | null;
  recipient_name?: string | null;
  recipient_edrpou?: string | null;
  recipient_iban?: string | null;
  amount: string;
  amount_currency?: string | null;  // ч.798
  exchange_rate?: string | null;
  payment_purpose?: string | null;
  comment?: string | null;
}

export type BankPaymentUpdate = Partial<BankPaymentCreate>;

export type BankPaymentExportFormat = 'auto' | '1c' | 'privat_csv' | 'pumb_csv';

// ч.766: платіжки податків для привʼязки до зарплатної платіжки.
export interface TaxCandidate {
  id: string; document_number: string; document_date: string | null; amount: string;
  posted: boolean; recipient_name: string | null; tax_type: 'PDFO' | 'WZ' | 'ESV' | null;
}

// ч.754: неоплачені документи Кадрів для «На підставі» (виплата ЗП / податків).
export interface BasisDocRow {
  doc_type: 'doc.payroll_payments' | 'doc.tax_remittances';
  id: string;
  document_number: string;
  document_date: string | null;
  period_year: number | null;
  period_month: number | null;
  total_amount: string;
  paid_amount: string;
  open_amount: string;
}

export const bankPayments = {
  taxCandidates: (params: { organization_id: string; payroll_payment_id?: string; posted?: boolean;
    q?: string; date_from?: string; date_to?: string }) =>
    api.get<TaxCandidate[]>('/bank-payments/tax-candidates', { params }).then((r) => r.data),
  basisDocs: (organization_id: string, kind: 'SALARY' | 'TAX') =>
    api.get<BasisDocRow[]>('/bank-payments/basis-docs', { params: { organization_id, kind } }).then((r) => r.data),
  nextNumber: (organization_id: string) =>
    api.get<{ next_number: string }>('/bank-payments/next-number', { params: { organization_id } }).then((r) => r.data),
  list: (params?: { organization_id?: string; contractor_id?: string; posted?: boolean; limit?: number
    needs_review?: boolean; bank_account_id?: string; currency_code?: string; operation_type?: string;
    date_from?: string;
    date_to?: string;
    pn_link?: 'with' | 'without';
    sent?: boolean;
    offset?: number;
    sort_by?: string;
    sort_dir?: 'asc' | 'desc'; }) =>
    api.get<Page<BankPaymentListItem>>('/bank-payments', { params }).then((r) => r.data),
  // ч.734: підсумок під ті ж фільтри (кількість + сума) — для рядка під вкладками.
  tabCounts: (organizationId?: string) =>
    api.get<{ review: number; saved: number; sent: number }>('/bank-payments/tab-counts', { params: { organization_id: organizationId || undefined } }).then((r) => r.data),
  summary: (params?: { organization_id?: string; contractor_id?: string; posted?: boolean;
    date_from?: string; date_to?: string; pn_link?: 'with' | 'without'; sent?: boolean;
    needs_review?: boolean; bank_account_id?: string; currency_code?: string }) =>
    api.get<{ count: number; sum_amount: string }>('/bank-payments/summary', { params }).then((r) => r.data),
  // ч.727: збережені списання → реєстр у клієнт-банк (формат авто за МФО IBAN:
  // Приват — csv формату НБУ, ПУМБ — csv ПУМБ; лише csv — рішення власника).
  exportBank: async (organizationId: string, ids: string[], format: BankPaymentExportFormat = 'auto') => {
    const r = await api.post<Blob>('/bank-payments/export-bank',
      { organization_id: organizationId, ids, format }, { responseType: 'blob' });
    const cd = String(r.headers['content-disposition'] ?? '');
    const m = /filename="?([^";]+)"?/.exec(cd);
    return {
      blob: r.data,
      filename: m?.[1] ?? 'payments.csv',
      missingIban: String(r.headers['x-missing-recipient-iban'] ?? '').split(',').filter(Boolean),
      bankFormat: String(r.headers['x-bank-format'] ?? ''),
    };
  },
  get: (id: string) => api.get<BankPayment>(`/bank-payments/${id}`).then((r) => r.data),
  create: (payload: BankPaymentCreate) =>
    api.post<BankPayment>('/bank-payments', payload).then((r) => r.data),
  update: (id: string, payload: BankPaymentUpdate) =>
    api.patch<BankPayment>(`/bank-payments/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/bank-payments/${id}`).then(() => undefined),
  post: (id: string) => api.post<BankPayment>(`/bank-payments/${id}/post`).then((r) => r.data),
  unpost: (id: string) => api.post<BankPayment>(`/bank-payments/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/bank-payments/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};
